import { FC, useState, useRef, useEffect } from 'react'
import styles from './TransformSleepSteps.module.scss'
import { Icon } from 'components/Icon'
import { Img } from 'components/basic/Img'
import { Colors } from 'components/WebEv/Colors/colors'
import cx from 'classnames'
import { Alarm, AutomaticTemp, coverSlides, podSlides, SleepInsights, Step } from './steps'
import { getVisibleAmount } from 'components/_utils/boundingBoxHelpers'
import { Button } from 'components/WebEv/Buttons'
import AnchorLink from 'react-anchor-link-smooth-scroll'

interface TransformSleepStepsProps {
	title: string
	steps: Step[]

	cta?: string
	ctaLink?: string
}

const TIME_PER_STEP = 3000

const setHeights = (refs: HTMLDivElement[]) => {
	for (const ref of refs) {
		const pChild = ref.children[0] as HTMLParagraphElement
		if (!pChild) continue
		ref.style.setProperty('--height', `${pChild.offsetHeight}px`)
	}
}

export const TransformSleepSteps: FC<TransformSleepStepsProps> = (props) => {
	const [activeStep, setActiveStep] = useState(0)
	const startTime = useRef(Date.now())
	const containerRef = useRef<HTMLDivElement>(null)
	const descriptionRefs = useRef<HTMLDivElement[]>([])

	useEffect(() => {
		let frame: number

		const TOTAL_TIME = props.steps.length * TIME_PER_STEP
		const loop = () => {
			frame = requestAnimationFrame(loop)

			const container = containerRef.current

			if (getVisibleAmount(container) > 0) {
				const time = Date.now() - startTime.current
				setActiveStep(Math.floor(time / TIME_PER_STEP) % props.steps.length)

				const t = time % TOTAL_TIME

				container.style.setProperty('--progress', `${((t / TOTAL_TIME) * 100).toFixed(2)}%`)
			}
		}

		frame = requestAnimationFrame(loop)

		return () => cancelAnimationFrame(frame)
	}, [])

	useEffect(() => {
		setHeights(descriptionRefs.current)

		window.addEventListener('resize', () => {
			setHeights(descriptionRefs.current)
		})
	}, [])

	return (
		<div
			className={styles.container}
			ref={containerRef}
		>
			<div className={styles.main_content}>
				<h2>{props.title}</h2>
				<ul className={styles.steps}>
					{props.steps.map((step, index) => (
						<li
							key={index}
							className={cx(styles.step_item, { [styles.step_item_active]: activeStep === index })}
							role={'button'}
							onClick={() => {
								startTime.current = Date.now() - index * TIME_PER_STEP
							}}
						>
							{step.icon && (
								<div className={styles.icon_container}>
									<Icon
										icon={step.icon}
										color={Colors.Neutral.black.hex}
									/>
								</div>
							)}
							<h3>{step.title}</h3>
							<div
								className={styles.description_wrapper}
								ref={(el) => {
									if (el) {
										descriptionRefs.current[index] = el
									}
								}}
							>
								<p id={`step-description-${index}`}>{step.description}</p>
							</div>

							{props.cta && (
								<div className={styles.text_cta_container}>
									<Button.TextLink
										id={`cta-for-transform-step-${index}`}
										href={props.ctaLink}
									>
										{props.cta}
									</Button.TextLink>
								</div>
							)}

							<Img
								src={step.image}
								alt={step.alt}
								className={styles.mobile_image}
								objectFit={'cover'}
							/>
						</li>
					))}
				</ul>
			</div>
			<div className={styles.desktop_image_stack}>
				{props.steps.map((step, index) => (
					<Img
						key={`desktop-image-${index}`}
						src={step.image}
						alt={step.alt}
						className={cx(styles.desktop_image, { [styles.desktop_image_active]: activeStep === index })}
						objectFit={'cover'}
						dprHeight={700}
					/>
				))}
			</div>
			{props.cta && (
				<div className={styles.cta_container}>
					<AnchorLink href={props.ctaLink}>
						<Button.Primary id={'transform-cta'}>{props.cta}</Button.Primary>
					</AnchorLink>
				</div>
			)}
		</div>
	)
}

export const TransformSleepStepsA = ({ href }) => (
	<TransformSleepSteps
		title={'Everything you need to sleep better'}
		steps={[AutomaticTemp, Alarm, SleepInsights]}
		cta={'Shop now'}
		ctaLink={href}
	/>
)

export const TransformSleepStepsPod = () => (
	<TransformSleepSteps
		title={'Transform your sleep in 4 easy steps'}
		steps={podSlides}
	/>
)

export const TransformSleepStepsCover = () => (
	<TransformSleepSteps
		title={'How to add the Pod Cover to your bed'}
		steps={coverSlides}
	/>
)
